/**
 * 品牌标的自动排版。
 *
 * ## 为什么在浏览器里画，不在服务端
 *
 * 生产服务器上 `fc-list` 是 **0 条** —— 一个字体都没有。服务端用 sharp 渲染
 * 文字会得到空白（不是报错，是安静地什么都不画）。要修就得装 100MB 以上的
 * 中文字体，并且给每台机器、每次重建镜像都记得装。
 *
 * 而客户点按钮的那一刻就在浏览器里，浏览器一定有中文字体（Windows 雅黑、
 * Mac 苹方、Android/Linux Noto）。画完导出 PNG 交给现有的直传链路，
 * 服务端一个字节都不用改。
 *
 * ## 为什么先生成 SVG 再转 PNG
 *
 * SVG 是预览和导出的**同一份**描述：预览直接塞进 `<img src="data:image/svg+xml…">`，
 * 导出时再画到 canvas。两套渲染路径必然走样（字号差一像素就会换行），
 * 一份描述不会。
 *
 * ## 为什么字一定不会错
 *
 * 这里没有任何生成模型参与排版 —— 名字是客户的字符串，字体是确定的字体，
 * 位置由公式算出来。可以用「换一版」穷举几十种组合，每一种都保证名字正确。
 */

export type BrandLayout = 'wordmark' | 'mark-left' | 'stacked' | 'badge';
export type BrandMarkKind =
  'ring' | 'diamond' | 'hexagon' | 'bars' | 'notch' | 'orbit' | 'grid' | 'wave';

export const BRAND_LAYOUTS: { key: BrandLayout; label: string; hint: string }[] = [
  { key: 'wordmark', label: '纯文字', hint: '只有名字，最克制' },
  { key: 'mark-left', label: '符号在左', hint: '最通用，页头横排放得下' },
  { key: 'stacked', label: '符号在上', hint: '名字短时更稳' },
  { key: 'badge', label: '色块标', hint: '深色页头也压得住' },
];

export const BRAND_MARKS: { key: BrandMarkKind; label: string }[] = [
  { key: 'ring', label: '圆环' },
  { key: 'diamond', label: '菱形' },
  { key: 'hexagon', label: '六边' },
  { key: 'notch', label: '缺角' },
  { key: 'bars', label: '横条' },
  { key: 'orbit', label: '轨道' },
  { key: 'grid', label: '方阵' },
  { key: 'wave', label: '波线' },
];

export interface BrandPalette {
  key: string;
  label: string;
  /** 文字与符号的颜色 */
  ink: string;
  /** 强调色（色块底的填充） */
  accent: string;
  /** 是否画一块实底（透明底的标压在深浅两种页头上都容易糊） */
  solid: boolean;
  /**
   * 保留 AI 图形的原色。
   *
   * 没有这一档之前，所有图形都会被 `tintArt` 刷成 `ink` 一色 —— 客户花一张图的钱，
   * 拿到一个和本地排版生成几乎一样的单色几何块，AI 这层就白做了。
   * 但原色也有原色的麻烦：它不跟主题走，压在深色页头上可能糊，所以配套
   * `BrandLogoOptions.artPlate` 给一块托底。
   */
  keepArt?: boolean;
}

export interface BrandLogoOptions {
  name: string;
  layout: BrandLayout;
  mark: BrandMarkKind;
  palette: BrandPalette;
  /**
   * AI 生成的图形（data URL）。
   *
   * 必须内联成 data URL 而不是外链：导出 PNG 时要把 SVG 画进 canvas，
   * 里面只要有跨域图片，canvas 立刻被污染，`toBlob` 直接抛 SecurityError。
   */
  artUrl?: string | null;
  /**
   * 图形底下的圆角托板，null = 不画。
   *
   * 只在用了 AI 图形、且图形本身与页头底色分不开时才给。它不是装饰：
   * 一张彩色图形压在同色系的深页头上，除了加一块反差底板没有别的解法 ——
   * 而"把彩色图形重新染色"会让它退回单色，那是另一个问题。
   */
  artPlate?: string | null;
  /**
   * 整张标（`full` 模式）：模型已经把图形和名字一起画进图里了。
   *
   * 有它时**不再排版**：不画内置符号、不画名字、不看版式和配色 ——
   * 那张图就是成品，我们只按它的原比例给个尺寸，必要时垫一块底板。
   *
   * 为什么不把它当成 `artUrl` 塞进 graphicBlock：那条路会 `clip` 成正方形。
   * 一张横条的整标裁成方的，字会被切掉一半。
   */
  lockup?: { url: string; width: number; height: number } | null;
  /**
   * 整张标的底板色，null = 透明。
   *
   * 整标是"死的一张图"：配色由模型定，我们没法像排版那样换个 ink 重画。
   * 所以它压在同色系的页头上就只能靠底板救 —— 底板是这里唯一的兜底手段。
   */
  lockupPlate?: string | null;
  /** 图标里的字。默认取名字的第一个字符 */
  initial?: string;
}

/**
 * 字体栈。
 *
 * 全中文优先：客户要么写中文名，要么写英文名，而**中文字体也带拉丁字母**
 * （雅黑的 Arial 那一档），一份栈就够，不用按语言分叉。
 */
const FONT_STACK =
  "'PingFang SC','Microsoft YaHei','Noto Sans SC','Source Han Sans SC','Hiragino Sans GB',system-ui,-apple-system,'Segoe UI',sans-serif";

/* ── 颜色工具 ───────────────────────────────────────────────────────── */

function normalizeHex(input: string | null | undefined): string | null {
  if (!input) return null;
  let value = input.trim();
  if (!value.startsWith('#')) value = `#${value}`;
  if (/^#[0-9a-f]{3}$/i.test(value)) {
    return `#${value[1]}${value[1]}${value[2]}${value[2]}${value[3]}${value[3]}`.toLowerCase();
  }
  return /^#[0-9a-f]{6}$/i.test(value) ? value.toLowerCase() : null;
}

/** 线性混合两个十六进制色。t=0 取 a，t=1 取 b */
export function mixHex(a: string, b: string, t: number): string {
  const pa = normalizeHex(a) ?? '#000000';
  const pb = normalizeHex(b) ?? '#ffffff';
  const channel = (offset: number) => {
    const av = parseInt(pa.slice(offset, offset + 2), 16);
    const bv = parseInt(pb.slice(offset, offset + 2), 16);
    return Math.round(av + (bv - av) * Math.max(0, Math.min(1, t)))
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(1)}${channel(3)}${channel(5)}`;
}

/**
 * 从站点品牌色派生可选的配色。
 *
 * 不给"彩虹色板"：客户没有设计能力时，选得越多越容易选难看。
 * 覆盖真实场景 —— 浅页头（墨色两档 + 品牌色）、深页头（**反白** + 实底三档）。
 *
 * `on-dark` 是后补的，因为漏掉它出了一个很难看的错：客户在深色页头上选了
 * 墨黑版，导出深色字，压上去整块消失。深底要的不是"墨黑加深一点"，
 * 而是**反白**，这是两套不同的配色，不能用同一份调色板硬凑。
 */
export function buildPalettes(
  brandColor: string | null | undefined,
  headerBg?: string | null,
): BrandPalette[] {
  const base = normalizeHex(brandColor) ?? '#2563eb';
  const deep = mixHex(base, '#000000', 0.3);
  /**
   * 「保留原色」这一档的文字色只能按页头定，不能按品牌色定 ——
   * 图形是什么颜色由模型决定，我们唯一能确定的是文字压在哪种底色上。
   */
  const onDark = headerBg ? relativeLuminance(headerBg) < 0.4 : false;
  return [
    { key: 'brand', label: '品牌色', ink: deep, accent: base, solid: false },
    { key: 'ink', label: '墨黑', ink: '#101828', accent: '#101828', solid: false },
    { key: 'graphite', label: '石墨', ink: '#3f3f3c', accent: '#5f5e5a', solid: false },
    // accent 给深品牌色而不是白：图标一定要有实底，底板取 accent，白底配白字等于没画
    { key: 'on-dark', label: '反白', ink: '#ffffff', accent: deep, solid: false },
    { key: 'solid-brand', label: '品牌底', ink: '#ffffff', accent: base, solid: true },
    { key: 'solid-ink', label: '墨黑底', ink: '#ffffff', accent: '#101828', solid: true },
    { key: 'solid-deep', label: '深品牌底', ink: '#ffffff', accent: deep, solid: true },
    // 只有挂了 AI 图形才有意义：其余几档都会把图形刷成单一色
    {
      key: 'art',
      label: '保留原色',
      ink: onDark ? '#ffffff' : '#101828',
      accent: base,
      solid: false,
      keepArt: true,
    },
  ];
}

/* ── 对比度 ─────────────────────────────────────────────────────────── */

/** sRGB 相对亮度（WCAG 的定义），用来判断"这套配色压在页头上还看不看得见" */
export function relativeLuminance(hex: string): number {
  const value = normalizeHex(hex) ?? '#000000';
  const channel = (offset: number) => {
    const c = parseInt(value.slice(offset, offset + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/** 两色的对比度，1–21。正文要 4.5，图形要 3，低于 2 基本就是"看不见" */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const light = Math.max(la, lb);
  const dark = Math.min(la, lb);
  return (light + 0.05) / (dark + 0.05);
}

/** 这套配色放在这个底色上会不会看不清 */
export function paletteLegibility(
  palette: BrandPalette,
  background: string,
): { ok: boolean; ratio: number; suggestion: string } {
  // 实底色块自带底板，只要底板与底色分得开就行，字不用再管
  const subject = palette.solid ? palette.accent : palette.ink;
  const ratio = contrastRatio(subject, background);
  const darkBg = relativeLuminance(background) < 0.4;
  return {
    ok: ratio >= 2.5,
    ratio,
    // 深底给反白，浅底给墨黑 —— 不用"再深一点/再浅一点"这种含糊话
    suggestion: darkBg ? 'on-dark' : 'ink',
  };
}

/**
 * 预览里"另一种底色"该用哪一块。
 *
 * 取墨色的反面：白标压在白底上等于没画，而第二块预览的作用正是让客户
 * 看清图形本身（有没有糊、有没有多余白框），不是再给他看一次看不见。
 * 放在这里而不是组件里，是因为色值要留在唯一允许写裸色的模块内。
 */
export function contrastBackdrop(ink: string): string {
  return relativeLuminance(ink) > 0.4 ? '#101828' : '#ffffff';
}

/* ── 文本度量 ───────────────────────────────────────────────────────── */

let measureCtx: CanvasRenderingContext2D | null | undefined;

/**
 * 浏览器里直接问画布要真宽。
 *
 * 逐字符估是不够的，而且**估错的方向最要命**：画布宽度是按估算值定的，
 * 估窄一点，右边那几个字母就被 viewBox 直接裁掉（第一版就是这样把
 * "Ningbo Precision Tools" 裁成了 "Ningbo Precision Too"）；估宽一点，
 * 整张图变宽，页头里按宽度上限反算出来的高度更小 —— 客户又觉得"太小"。
 *
 * 画布上量的和 SVG 里画的必须是同一套字体：`FONT_STACK` + 600 字重，
 * 与 `textBlock` 写进 SVG 的属性逐字对应。
 */
function measureWithCanvas(text: string, fontSize: number): number | null {
  if (typeof document === 'undefined') return null;
  if (measureCtx === undefined) {
    measureCtx = document.createElement('canvas').getContext('2d');
  }
  if (!measureCtx) return null;
  try {
    measureCtx.font = `600 ${fontSize}px ${FONT_STACK}`;
    const width = measureCtx.measureText(text).width;
    return Number.isFinite(width) && width > 0 ? width : null;
  } catch {
    return null;
  }
}

/**
 * 一段文字在给定字号下的宽度。
 *
 * 浏览器里返回画布的实测值；没有 DOM（测试、Node 脚本）时退回逐字符估算
 * —— 汉字/全角按 1em，拉丁字母按 0.56em，空格按 0.3em，这是常见无衬线体的均值。
 * 估算比实测**偏窄**，所以拿它定画布时要多留一点余量（见 `layoutLogo`）。
 */
export function estimateTextWidth(text: string, fontSize: number): number {
  const measured = measureWithCanvas(text, fontSize);
  if (measured !== null) return measured;
  let em = 0;
  for (const ch of text) {
    if (/[\u2e80-\u9fff\uf900-\ufaff\uff00-\uffef]/.test(ch)) em += 1;
    else if (/\s/.test(ch)) em += 0.3;
    else if (/[iljI.,:;'|]/.test(ch)) em += 0.28;
    else if (/[mMW@]/.test(ch)) em += 0.86;
    else em += 0.56;
  }
  return em * fontSize;
}

/**
 * 画布宽度取哪个数。
 *
 * 文字是左对齐起笔的，画布窄一点，右边缘那几个字母就被 viewBox 直接裁掉
 * （第一版正是这样把 "Ningbo Precision Tools" 裁成 "Ningbo Precision Too"）。
 * 所以 `text-anchor="start"` 的这一侧必须留余量：浏览器里实测已经准，
 * 只补一个抗锯齿的零头；没有 DOM 时逐字符估算偏窄约 7%，多留一成。
 */
function textWidthOf(name: string, size: number): number {
  const width = estimateTextWidth(name, size);
  const slack = typeof document === 'undefined' ? 1.12 : 1;
  return Math.ceil(width * slack) + Math.round(size * 0.12);
}

/** 在给定宽度内选出最大的可用字号 */
export function fitFontSize(
  text: string,
  maxWidth: number,
  maxSize: number,
  minSize: number,
): number {
  if (!text) return maxSize;
  // 一次线性求出理想字号，向下取整到 1px，再夹到区间里
  const ideal = Math.floor(maxWidth / Math.max(0.5, estimateTextWidth(text, 1)));
  return Math.max(minSize, Math.min(maxSize, ideal));
}

/* ── 符号 ───────────────────────────────────────────────────────────── */

/** 24×24 坐标系里画一个符号，颜色由调用方给（不用 currentColor，见调用点注释） */
export function markSvg(kind: BrandMarkKind, color: string): string {
  switch (kind) {
    case 'ring':
      return `<circle cx="12" cy="12" r="8.4" fill="none" stroke="${color}" stroke-width="3.2"/>`;
    case 'diamond':
      return `<path d="M12 2.4L21.6 12L12 21.6L2.4 12Z" fill="${color}"/>`;
    case 'hexagon':
      return `<path d="M12 2.2L20.5 7.1V16.9L12 21.8L3.5 16.9V7.1Z" fill="${color}"/>`;
    case 'notch':
      return `<path d="M3 3H14.4L21 9.6V21H3Z" fill="${color}"/>`;
    case 'bars':
      return (
        `<rect x="3" y="5.6" width="18" height="3.1" rx="1.55" fill="${color}"/>` +
        `<rect x="3" y="10.45" width="11.6" height="3.1" rx="1.55" fill="${color}"/>` +
        `<rect x="3" y="15.3" width="18" height="3.1" rx="1.55" fill="${color}"/>`
      );
    case 'orbit':
      return (
        `<circle cx="12" cy="12" r="3.7" fill="${color}"/>` +
        `<ellipse cx="12" cy="12" rx="10" ry="4.8" fill="none" stroke="${color}" stroke-width="1.6" transform="rotate(-28 12 12)"/>` +
        `<ellipse cx="12" cy="12" rx="10" ry="4.8" fill="none" stroke="${color}" stroke-width="1.6" transform="rotate(28 12 12)"/>`
      );
    case 'grid':
      return (
        `<rect x="3" y="3" width="8.2" height="8.2" rx="1.6" fill="${color}"/>` +
        `<rect x="12.8" y="3" width="8.2" height="8.2" rx="1.6" fill="${color}"/>` +
        `<rect x="3" y="12.8" width="8.2" height="8.2" rx="1.6" fill="${color}"/>` +
        `<rect x="12.8" y="12.8" width="8.2" height="8.2" rx="1.6" fill="${color}" opacity="0.45"/>`
      );
    case 'wave':
      return `<path d="M2 16.4C5.8 7.6 9.8 7.6 12 12.4C14.2 17.2 18.2 17.2 22 8.4" fill="none" stroke="${color}" stroke-width="3" stroke-linecap="round"/>`;
  }
}

/* ── SVG 组装 ───────────────────────────────────────────────────────── */

const escapeXml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * 有底板时，图形占外框的比例。
 *
 * 0.78 是图标类 logo 里最常见的占比：四周各留 11% 的呼吸位，
 * 板子看起来才像"托"，不像"框"。图形自己一点没缩 —— 底板是往外撑的。
 * 再大就贴边（板子变成一条描边），再小就像把缩略图贴在方块上。
 */
const ART_INSET = 0.82;

/**
 * 一个符号/一张 AI 图的绘制块。
 *
 * 有 AI 图形就用它（裁成圆角方块），没有就画内置矢量符号。
 * `size` 是外框边长，图形按 `preserveAspectRatio="xMidYMid meet"` 居中放 ——
 * 模型出的图多是正方形，抠图后也已经 contain 过，不会再变形。
 */
function graphicBlock(
  options: BrandLogoOptions,
  x: number,
  y: number,
  size: number,
  color: string,
): string {
  const radius = Math.round(size * 0.22);
  if (options.artUrl) {
    // id 带上尺寸：同一份 SVG 里若出现两个图形，固定 id 会让后一个裁成前一个的形状
    const clipId = `artclip-${x}-${y}-${size}`;
    const plate = options.artPlate ?? null;
    const inner = plate ? Math.round(size * ART_INSET) : size;
    const offset = plate ? Math.round((size - inner) / 2) : 0;
    const innerX = x + offset;
    const innerY = y + offset;
    const innerRadius = Math.round(inner * 0.18);
    return (
      (plate
        ? `<rect x="${x}" y="${y}" width="${size}" height="${size}" rx="${radius}" fill="${plate}"/>`
        : '') +
      `<defs><clipPath id="${clipId}"><rect x="${innerX}" y="${innerY}" width="${inner}" height="${inner}" rx="${innerRadius}"/></clipPath></defs>` +
      `<image href="${options.artUrl}" x="${innerX}" y="${innerY}" width="${inner}" height="${inner}" preserveAspectRatio="xMidYMid meet" clip-path="url(#${clipId})"/>`
    );
  }
  const scale = size / 24;
  return `<g transform="translate(${x} ${y}) scale(${scale})">${markSvg(options.mark, color)}</g>`;
}

function textBlock(
  text: string,
  x: number,
  y: number,
  size: number,
  color: string,
  anchor: 'start' | 'middle',
): string {
  return (
    `<text x="${x}" y="${y}" font-family="${FONT_STACK}" font-size="${size}" font-weight="600" ` +
    `fill="${color}" text-anchor="${anchor}" dominant-baseline="central" ` +
    `letter-spacing="0">${escapeXml(text)}</text>`
  );
}

/**
 * 内容框四周的透明留白，按内容高度取比例。
 *
 * 这个数**直接影响客户看到的标有多大**。页头是按整张图的外框约束的，
 * 图里留的空白全部从标的实际尺寸里扣：上一版画布固定 480×160，而内容只占
 * 高度的 54%，页头给 62px 时真正落地的只有 33px —— 客户选了「大」还是觉得小，
 * 就是这么来的。所以画布改成**紧贴内容**，只留一点点呼吸位。
 */
const LOGO_PAD_RATIO = 0.06;

/** 符号边长相对字号。1.22 倍字号 ≈ 1.7 倍大写字高，是"标＋字"里最常见的配比 */
const MARK_PER_EM = 1.22;

/**
 * 加了底板之后的符号外框倍率。
 *
 * 底板自己占掉 26% 的边，如果外框还按 1.22 走，图形会被压到 0.9em ——
 * 客户刚抱怨过"标偏小"，不能再缩一次。所以外框放大到 1.5em，
 * 扣掉呼吸位后图形仍是 1.11em，视觉大小和没底板时基本一致。
 */
const MARK_PER_EM_PLATE = 1.5;

interface LogoParts {
  /** 内容框（色块标含它自己的内边距，不含四周透明留白） */
  width: number;
  height: number;
  /** 色块底。null = 透明底 */
  plate: string | null;
  body: string;
}

/**
 * 量出内容框再画。
 *
 * 上一版的错不只是留白：`mark-left` 把符号写死 96px、字号交给 `fitFontSize`
 * 去压，长名字一压到 26px，符号就成了文字的 5 倍高 —— 客户说的"比例不匹配"
 * 指的就是这个。现在**符号跟着字号走**，名字变长两者一起缩，比例永远一致。
 */
function layoutLogo(options: BrandLogoOptions): LogoParts {
  const name = options.name.trim() || 'Your Brand';
  // badge 无论选了哪套配色都要一块底：白字必须有底色托着，否则在浅页头上消失
  const solid = options.palette.solid || options.layout === 'badge';
  const ink = solid ? '#ffffff' : options.palette.ink;
  // 有底板时符号要放大，否则呼吸位把图形吃掉一圈
  const perEm = options.artPlate ? MARK_PER_EM_PLATE : MARK_PER_EM;

  const plateOf = (w: number, h: number, radius: number): string =>
    `<rect x="0" y="0" width="${w}" height="${h}" rx="${Math.round(radius)}" fill="${options.palette.accent}"/>`;

  if (options.layout === 'wordmark') {
    // 纯文字没有符号撑高度，宽度上限给得宽松些：横排字就是它的全部信息
    const size = fitFontSize(name, 520, 68, 28);
    const w = textWidthOf(name, size);
    // 行高留到 0.92：g/y/p 的下伸部分不能被裁掉
    const h = Math.round(size * 0.92);
    return {
      width: w,
      height: h,
      plate: solid ? plateOf(w, h, h * 0.22) : null,
      body: textBlock(name, w / 2, h / 2, size, ink, 'middle'),
    };
  }

  if (options.layout === 'stacked') {
    const size = fitFontSize(name, 300, 52, 22);
    const mark = Math.round(size * perEm * 1.1);
    const gap = Math.round(size * 0.3);
    const textH = Math.round(size * 0.92);
    const textW = textWidthOf(name, size);
    const w = Math.max(mark, textW);
    const h = mark + gap + textH;
    return {
      width: w,
      height: h,
      plate: solid ? plateOf(w, h, Math.min(w, h) * 0.18) : null,
      body:
        graphicBlock(options, Math.round((w - mark) / 2), 0, mark, ink) +
        textBlock(name, w / 2, mark + gap + textH / 2, size, ink, 'middle'),
    };
  }

  if (options.layout === 'badge') {
    // 底块自己就是"内容"，内边距算在内容框里，外面不再套透明边
    const size = fitFontSize(name, 300, 54, 22);
    const mark = Math.round(size * perEm);
    const gap = Math.round(size * 0.4);
    const textW = textWidthOf(name, size);
    const padX = Math.round(size * 0.66);
    const padY = Math.round(size * 0.5);
    const w = padX * 2 + mark + gap + textW;
    const h = padY * 2 + mark;
    return {
      width: w,
      height: h,
      plate: plateOf(w, h, h * 0.26),
      body:
        graphicBlock(options, padX, padY, mark, ink) +
        textBlock(name, padX + mark + gap, padY + mark / 2, size, ink, 'start'),
    };
  }

  // mark-left（默认）：符号在左、名字在右
  const size = fitFontSize(name, 330, 58, 24);
  const mark = Math.round(size * perEm);
  const gap = Math.round(size * 0.38);
  const textW = textWidthOf(name, size);
  const w = mark + gap + textW;
  const h = Math.max(mark, Math.round(size * 0.92));
  const markY = Math.round((h - mark) / 2);
  return {
    width: w,
    height: h,
    plate: solid ? plateOf(w, h, h * 0.2) : null,
    body:
      graphicBlock(options, 0, markY, mark, ink) +
      // 左对齐时 anchor=start，垂直靠 baseline central 与符号中线对齐
      textBlock(name, mark + gap, h / 2, size, ink, 'start'),
  };
}

/**
 * 整张标的基准高度（内容框单位）。
 *
 * 页头最终按 CSS 的 `max-block-size` 约束高度，所以这个数只是个比例基准 ——
 * 但它不能写太小：导出时 `logoSize` 要拿它乘 2 倍算画布，
 * 40 出来就是一张 80px 高的糊图。
 */
const LOCKUP_HEIGHT = 64;

/**
 * 有底板时整标四周的呼吸位，按高度取比例。
 *
 * 0.16 而不是更宽松的值：底板占掉多少，页头给的那点高度里就少多少 ——
 * 客户刚因为"标太小"提过意见，底板是兜底，不该顺手再缩一号。
 * 比它再小，板子就贴边了，看起来像描边而不是托底。
 */
const LOCKUP_PAD_RATIO = 0.16;

/**
 * 整张标：模型已经把图形和名字都画好了，这里只做两件事 ——
 * 按原比例给它一个尺寸，以及必要时垫一块底板。
 *
 * 宽度不设上限：页头的 `max-inline-size` 会处理超宽。在这里提前压宽，
 * 等于让一张本来就宽的标再小一号。
 */
function composeLockup(
  lockup: { url: string; width: number; height: number },
  plate: string | null,
): { width: number; height: number; svg: string } {
  const ratio = lockup.width / lockup.height;
  const height = LOCKUP_HEIGHT;
  const width = Math.round(height * ratio);
  const pad = plate ? Math.round(height * LOCKUP_PAD_RATIO) : 0;
  const boxW = width + pad * 2;
  const boxH = height + pad * 2;
  const radius = Math.round(boxH * 0.24);
  const body =
    (plate
      ? `<rect x="0" y="0" width="${boxW}" height="${boxH}" rx="${radius}" fill="${plate}"/>`
      : '') +
    `<image href="${lockup.url}" x="${pad}" y="${pad}" width="${width}" height="${height}" ` +
    `preserveAspectRatio="xMidYMid meet"/>`;
  return {
    width: boxW,
    height: boxH,
    svg:
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${boxW} ${boxH}" ` +
      `width="${boxW}" height="${boxH}">${body}</svg>`,
  };
}

function composeLogo(options: BrandLogoOptions): { width: number; height: number; svg: string } {
  // 整标优先：它出现时上面的版式/符号/配色全部无关，客户看到的就是模型那张图
  if (options.lockup) return composeLockup(options.lockup, options.lockupPlate ?? null);
  const parts = layoutLogo(options);
  // 色块标不需要外圈透明边：底块本身就是边界，再套一圈会让它显得有缝隙
  const pad = parts.plate ? 0 : Math.round(parts.height * LOGO_PAD_RATIO);
  const width = parts.width + pad * 2;
  const height = parts.height + pad * 2;
  const inner = (parts.plate ?? '') + parts.body;
  const wrapped = pad ? `<g transform="translate(${pad} ${pad})">${inner}</g>` : inner;
  return {
    width,
    height,
    svg:
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" ` +
      `width="${width}" height="${height}">` +
      wrapped +
      `</svg>`,
  };
}

/**
 * 页头用 Logo。画布紧贴内容，尺寸随版式和名字长度变。
 *
 * 导出前要按这个尺寸算像素（见 `logoSize`）—— 写死 480×160 会把紧贴后的图
 * 拉回老样子：两边补透明，客户看到的又变小了。
 */
export function buildLogoSvg(options: BrandLogoOptions): string {
  return composeLogo(options).svg;
}

/** Logo 的像素尺寸。导出时按它 × 缩放倍数开画布 */
export function logoSize(options: BrandLogoOptions): { width: number; height: number } {
  const { width, height } = composeLogo(options);
  return { width, height };
}

/**
 * 浏览器图标：256×256。
 *
 * 与 Logo 的区别是**一定有实底**：favicon 出现在浏览器标签栏，那里底色由用户的
 * 主题决定（浅色/深色都在），透明底的浅色图案在浅色标签栏上会直接消失。
 * 内容优先用 AI 图形；没有就用名字首字；再没有就用内置符号。
 */
export function buildIconSvg(options: BrandLogoOptions): string {
  const S = 256;
  const ink = '#ffffff';
  // 图标里的字恒为白，所以底板必须是深的。反白配色（accent 会被派生成深品牌色）
  // 之外还要兜一层：客户把品牌色配成浅黄时，白字压在浅底上一样看不见。
  const raw = options.palette.solid
    ? options.palette.accent
    : mixHex(options.palette.accent, '#000000', 0.18);
  const plate = relativeLuminance(raw) > 0.45 ? '#101828' : raw;
  const background = `<rect x="0" y="0" width="${S}" height="${S}" rx="56" fill="${plate}"/>`;

  if (options.artUrl) {
    // 图标自己已经有一块实底了，再叠一层 artPlate 就是板套板
    const iconOptions = options.artPlate ? { ...options, artPlate: null } : options;
    return (
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${S} ${S}" width="${S}" height="${S}">` +
      background +
      graphicBlock(iconOptions, 40, 40, 176, ink) +
      `</svg>`
    );
  }

  const initial = (options.initial ?? options.name.trim().charAt(0)).trim();
  if (initial) {
    return (
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${S} ${S}" width="${S}" height="${S}">` +
      background +
      textBlock(initial, S / 2, S / 2 + 6, 132, ink, 'middle') +
      `</svg>`
    );
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${S} ${S}" width="${S}" height="${S}">` +
    background +
    graphicBlock(options, 64, 64, 128, ink) +
    `</svg>`
  );
}

/* ── 导出 ───────────────────────────────────────────────────────────── */

/**
 * SVG → PNG。
 *
 * 走 canvas 而不是让服务端转换：服务端没有字体（见文件头）。这里用 data URL
 * 而不是 blob URL，是为了避开"撤销 URL 的时机"这类竞态 —— 图片解码是异步的。
 */
export async function renderSvgToPng(svg: string, width: number, height: number): Promise<Blob> {
  // 把 width/height 换成目标像素再进 `<img>`：浏览器会按这份尺寸**矢量重绘**，
  // 导出 2 倍图时不会拿到一张放大过位图。只改 viewBox 外的这两个属性，
  // 画布比例由调用方保证与 viewBox 相同。
  const scaled = svg.replace(
    /(<svg[^>]*?)width="[\d.]+"\s+height="[\d.]+"/,
    `$1width="${width}" height="${height}"`,
  );
  const source = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(scaled)}`;
  const image = new Image();
  image.decoding = 'sync';
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('图片渲染失败，换一版试试'));
    image.src = source;
  });

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('当前浏览器不支持画布，换 Chrome / Edge 试试');
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(image, 0, 0, width, height);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('导出图片失败，换一版试试'));
    }, 'image/png');
  });
}

/** 预览用的 data URL。与导出走同一份 SVG，所见即所得 */
export function svgToDataUrl(svg: string): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * 把 AI 图形染成指定颜色，保留原本的线条结构。
 *
 * 模型给的是**深色线稿 + 白底**（提示词里我们只要求"一个图形"，没资格指定颜色），
 * 而客户可能选了反白配色 —— 直接把线稿塞进去，深色页头上就是一团黑。
 *
 * 做法是**把亮度当遮罩**，不是整张刷成单色：
 *   alpha' = alpha × (1 − 亮度)
 * 深的地方留下、白的地方变透明。整张刷色看着更简单，但闭口图形的内部白
 * （圆环里那一圈、齿轮中间的孔）是连不通的，抠图阶段留着，刷色后就成了
 * 一块实心圆 —— 线条全部消失，比原问题更糟。
 *
 * 顺带解决白底没抠干净的情况：白底亮度接近 1，遮罩后自然变透明，
 * 不需要再判断四角。
 */
/**
 * AI 图形要不要垫底板、垫什么色。
 *
 * 只在图形真的和页头底色分不开时才垫。大多数浅色页头上彩色图形本身就够显眼，
 * 多一块板反而像贴纸 —— 底板是兜底，不是默认。
 *
 * 2.2 这个阈值比正文的 4.5 低得多：logo 是"看见就行"的图形，不是要读的字。
 */
export function artPlateFor(artInk: string | null, headerBg: string): string | null {
  // 分析不出图形主色时按页头亮度兜底：深页头一律垫白板，浅页头不垫
  if (!artInk) return relativeLuminance(headerBg) < 0.4 ? '#ffffff' : null;
  return contrastRatio(artInk, headerBg) < 2.2 ? contrastBackdrop(headerBg) : null;
}

/**
 * 读一张图的实际宽高。
 *
 * 整标（`full` 模式）的比例完全由模型决定，前端不能假设它是方的 ——
 * 猜错就会把横条按方图摆：字被压扁，或者四周补一堆透明把标挤小。
 * 上一轮"选了「大」还是小"就是画布留白造成的，这里是同一个坑的另一面。
 */
export async function measureImage(dataUrl: string): Promise<{ width: number; height: number }> {
  const image = new Image();
  image.decoding = 'sync';
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('这张图读不出来'));
    image.src = dataUrl;
  });
  return { width: image.naturalWidth, height: image.naturalHeight };
}

/**
 * 读一张图的"整体色"，用来判断它压在页头上够不够显眼。
 *
 * 缩到 48×48 再平均，不是偷懒：256² 逐个像素在移动端要几十毫秒，
 * 而这个值只用来做一个"垫不垫底板"的二选一，精度到这个量级就够。
 * 按 alpha 加权是为了别让边缘的半透明像素把结果拉向白色。
 */
export async function artInkOf(dataUrl: string, size = 48): Promise<string> {
  const image = new Image();
  image.decoding = 'sync';
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('图形读不出来'));
    image.src = dataUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('当前浏览器不支持画布，换 Chrome / Edge 试试');
  ctx.drawImage(image, 0, 0, size, size);

  const { data } = ctx.getImageData(0, 0, size, size);
  let r = 0;
  let g = 0;
  let b = 0;
  let weight = 0;
  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3]!;
    if (alpha < 128) continue;
    r += data[i]! * alpha;
    g += data[i + 1]! * alpha;
    b += data[i + 2]! * alpha;
    weight += alpha;
  }
  if (!weight) return '#101828';
  const channel = (value: number): string =>
    Math.min(255, Math.round(value / weight))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

export async function tintArt(dataUrl: string, color: string, size = 256): Promise<string> {
  const image = new Image();
  image.decoding = 'sync';
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve();
    image.onerror = () => reject(new Error('图形读不出来'));
    image.src = dataUrl;
  });

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('当前浏览器不支持画布，换 Chrome / Edge 试试');
  ctx.drawImage(image, 0, 0, size, size);

  const target = normalizeHex(color) ?? '#ffffff';
  const tr = parseInt(target.slice(1, 3), 16);
  const tg = parseInt(target.slice(3, 5), 16);
  const tb = parseInt(target.slice(5, 7), 16);

  const frame = ctx.getImageData(0, 0, size, size);
  const data = frame.data;
  for (let i = 0; i < data.length; i += 4) {
    const alpha = data[i + 3]!;
    if (alpha === 0) continue;
    // 感知亮度；直接用平均值会让蓝色线条显得比实际更亮
    const lum = (0.2126 * data[i]! + 0.7152 * data[i + 1]! + 0.0722 * data[i + 2]!) / 255;
    data[i] = tr;
    data[i + 1] = tg;
    data[i + 2] = tb;
    data[i + 3] = Math.round(alpha * (1 - lum));
  }
  ctx.putImageData(frame, 0, 0);
  return canvas.toDataURL('image/png');
}
